// services/frontend/src/components/composed/PhotoDetailsSection/PhotoDetailsSection.tsx
// Feature 649: Component that encapsulates photo details (description, EXIF, task button)
// Feature 081: Version history and comparison

import { useState } from 'react';
import { Heading } from '../../atoms/Heading/Heading';
import { PhotoMetadata } from '../../PhotoMetadata/PhotoMetadata';
import { TaskCreationButton } from '../../TaskCreationButton';
import { VersionHistory } from '../../VersionHistory';
import { ImageCompare } from '../../ImageCompare';
import { usePhotoVersions, type PhotoVersion } from '../../../hooks/graphql/usePhotoVersionsGraphQL';
import type { PhotoExifMetadata } from '../../../types/exif';
import type { Photo } from '../../../types/photo';

export interface PhotoDetailsData {
  description?: string;
  exif?: PhotoExifMetadata;
}

export interface PhotoDetailsSectionProps {
  /** The photo ID */
  photoId: string;
  /** Session ID for task creation (optional) */
  sessionId?: string;
  /** Photo details data (description and EXIF) */
  photoData?: PhotoDetailsData;
  /** Full photo object for metadata display */
  photo?: Photo | any; // Supports Photo or PhotoItem2ViewModel
  /** Feature 081: Callback when a version is selected */
  onVersionSelect?: (version: PhotoVersion) => void;
  /** Feature 081: Currently selected version ID */
  selectedVersionId?: string | null;
}

export function PhotoDetailsSection({
  photoId,
  sessionId,
  photoData,
  photo,
  onVersionSelect,
  selectedVersionId,
}: PhotoDetailsSectionProps) {
  const [showCompare, setShowCompare] = useState(false);
  const { versions } = usePhotoVersions(photoId);

  const hasMultipleVersions = versions.length >= 2;
  const hasPhotoDetails = photoData && (photoData.description || photoData.exif);

  // Don't render if no data to display AND no versions
  // Feature 081: Show section if multiple versions exist (for version switcher)
  if (!hasPhotoDetails && !hasMultipleVersions) {
    return null;
  }

  return (
    <section className="pb-4 border-b border-border space-y-1">
      <div className="flex items-center justify-between mb-2">
        <Heading level="h5">Photo Details</Heading>
        {/* Feature 648: Task creation button for self-initiated comment task */}
        {/* Only show when sessionId is available (required for photo_in_critique target) */}
        {sessionId && <TaskCreationButton photoId={photoId} sessionId={sessionId} />}
      </div>

      {/* Description */}
      {photoData?.description && (
        <p className="text-sm text-foreground leading-snug">{photoData.description}</p>
      )}

      {/* EXIF Data - using PhotoMetadata component */}
      <PhotoMetadata
        photoData={photo || (photoData?.exif ? {
          id: photoId,
          globalId: photoId,
          metadata: photoData.exif
        } as Partial<Photo> : undefined)}
        variant="full"
      />

      {/* Version History (Feature 081) - shown below EXIF data */}
      <div className="pt-2">
        <div className="flex items-center justify-between mb-2">
          <VersionHistory
            photoId={photoId}
            compact={true}
            selectedVersionId={selectedVersionId}
            onVersionSelect={onVersionSelect}
          />
          {/* Compare button - only shown when multiple versions exist */}
          {hasMultipleVersions && (
            <button
              onClick={() => setShowCompare(!showCompare)}
              className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                showCompare
                  ? 'bg-primary text-white'
                  : 'bg-surface-2 text-foreground hover:bg-surface-3'
              }`}
            >
              {showCompare ? 'Hide' : 'Compare'}
            </button>
          )}
        </div>

        {/* ImageCompare - shown when Compare button clicked (Feature 081) */}
        {showCompare && hasMultipleVersions && (
          <div className="mt-2 bg-surface-2 rounded-lg p-2 h-64">
            <ImageCompare
              versions={versions}
              className="h-full"
            />
          </div>
        )}
      </div>
    </section>
  );
}
